import {
  Button,
  Checkbox,
  Fieldset,
  Flexbox,
  Icon,
  Select,
  Spacer,
  Spinner,
} from '@components/common'
import { useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { ReportFiltersProps } from './ReportFilter.types'
import ReportFilterInput from './ReportFilterInput'
import useReportFilters from './useReportFilters'
import { reportFilterDefaultSearchTypes } from './report-filter-fields'

const ReportFilters = ({
  filterFields,
  includesTotals = true,
  filtersTemplateData,
}: ReportFiltersProps) => {
  const {
    isLoadingFields,
    state,
    hasFilters,
    getFilterField,
    handleFieldChange,
    insertNewFilter,
    removeFilter,
    getFieldSelectOptions,
    handleSingleValueChange,
    handleSearchTypeChange,
    getSearchTypeFieldOptions,
    getSingleFieldValue,
    handleRangeValueChange,
    getRangeFieldValue,
    getMultiSelectValue,
    handleMultiSelectChange,
    handleCheckboxChange,
  } = useReportFilters({ filterFields, filtersTemplateData })

  // update form filter value when state changes
  const { setValue } = useFormContext()

  useEffect(() => {
    setValue('filter', state)
  }, [setValue, state])

  // Check if the last filter has a field selected (to enable adding new filter)
  const canAddNewFilter =
    state.length === 0 ||
    (state[state.length - 1]?.Name && state[state.length - 1].Name !== '')

  return (
    <Fieldset legend="Filters and Sorting">
      {!hasFilters && (
        <Button
          onClick={() => insertNewFilter()}
          appearance="primary"
          icon={<Icon name="PlusCircle" />}
        >
          Add Filter or Sort
        </Button>
      )}

      {hasFilters &&
        (isLoadingFields ? (
          <Flexbox height={80} alignItems="center" justifyContent="center">
            <Spinner />
          </Flexbox>
        ) : (
          <Flexbox flexDirection="column" gap={16}>
            {state.map((filter, index) => (
              <Flexbox
                key={index}
                alignItems="center"
                gap={12}
                style={{
                  padding: '12px',
                  borderRadius: '6px',
                  backgroundColor: '#fafafa',
                  border: '1px solid #e1e5e9',
                  flexWrap: 'wrap',
                }}
              >
                <Flexbox
                  flexDirection="column"
                  style={{ flex: '1 1 200px', minWidth: '200px' }}
                >
                  <Select
                    options={getFieldSelectOptions(index)}
                    name={`report-filter-${index}`}
                    value={{
                      label: getFieldSelectOptions(index).find(
                        option => option.value === state[index].Name
                      )?.label,
                      value: state[index].Name,
                    }}
                    onChange={(option, actionMeta, r) =>
                      handleFieldChange(option, actionMeta, index)
                    }
                    label="Field"
                  />
                </Flexbox>

                {state[index].Name === 'AssetID' && (
                  <Flexbox
                    flexDirection="column"
                    style={{ flex: '1 1 120px', minWidth: '120px' }}
                  >
                    <Select
                      options={reportFilterDefaultSearchTypes}
                      name={`report-search-type-${index}`}
                      value={{
                        label: getSearchTypeFieldOptions(index).find(
                          option => option.value === state[index].SearchType
                        )?.label,
                        value: state[index].SearchType,
                      }}
                      onChange={(option, actionMeta, r) =>
                        handleSearchTypeChange(option, actionMeta, index)
                      }
                      label="Search Type"
                    />
                  </Flexbox>
                )}

                <Flexbox style={{ flex: '2 1 150px', minWidth: '150px' }}>
                  <ReportFilterInput
                    state={state}
                    handleSingleValueChange={handleSingleValueChange}
                    filterField={getFilterField(filter)}
                    index={index}
                    getSingleFieldValue={getSingleFieldValue}
                    handleRangeValueChange={handleRangeValueChange}
                    getRangeFieldValue={getRangeFieldValue}
                    getMultiSelectValue={getMultiSelectValue}
                    handleMultiSelectChange={handleMultiSelectChange}
                  />
                </Flexbox>

                {includesTotals && (
                  <Flexbox
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="flex-end"
                    style={{ minWidth: '60px', marginTop: '15px' }}
                  >
                    <Checkbox
                      label="Total"
                      checked={state[index].Total}
                      onChange={e => handleCheckboxChange(e, index)}
                      disabled={!getFilterField(filter)}
                    />
                  </Flexbox>
                )}

                <Button
                  onClick={() => removeFilter(index)}
                  icon={<Icon name="Trash2" />}
                  shape="square"
                  appearance="text"
                  style={{ marginTop: '15px', color: 'var(--danger-color)' }}
                />
              </Flexbox>
            ))}

            <Flexbox justifyContent="flex-start" alignItems="center" gap={4}>
              <Button
                onClick={() => insertNewFilter()}
                appearance="primary"
                icon={<Icon name="PlusCircle" />}
                disabled={!canAddNewFilter}
              >
                Add new filter
              </Button>
              {!canAddNewFilter && (
                <Flexbox
                  alignItems="center"
                  justifyContent="center"
                  gap={4}
                  style={{ color: '#666', fontSize: '14px' }}
                >
                  <Icon name="Info" style={{ width: '18px', height: '18px' }} />
                  <span>Select a field in the current filter to add another</span>
                </Flexbox>
              )}
            </Flexbox>
          </Flexbox>
        ))}
    </Fieldset>
  )
}
export default ReportFilters
